export class ClientDateHandler {
    dateValue: null | Date;
    dateIsNull: boolean;
    isUTC: boolean;

    constructor() {
        this.dateValue = null;
        this.dateIsNull = false;
        this.isUTC = false;
    }

    resetDate = () => {
        this.dateValue = new Date();
    };

    setDateValue = (dateValue: null | Date, isUTC: boolean) => {
        this.dateValue = dateValue;
        this.isUTC = isUTC;
    };

    getLocalDate = (): Date | null => {
        const date = this.getDate(false);
        return date;
    };

    getUTCDate = (): Date | null => {
        const date = this.getDate(true);
        return date;
    };

    getDate = (isUtc: boolean): Date | null => {
        if (this.dateIsNull) return null;

        const years = this.getYear(isUtc);
        const month = this.getMonth(isUtc) - 1;
        const day = this.getDay(isUtc);
        const hours = this.getHours(isUtc);
        const minutes = this.getMinutes(isUtc);
        const seconds = this.getSeconds(isUtc);

        const date = new Date(years, month, day, hours, minutes, seconds);
        return date;
    };

    getJSON = () => {
        const dateObject = {
            value: this.formatDate(),
            utc: this.getUTC() ? '1' : '0'
        };

        const json = JSON.stringify(dateObject);
        return json;
    };

    formatDate = () => {
        if (this.dateValue == null) return '!null';

        const years = this.getYear();
        const month = this.getMonth();
        const day = this.getDay();
        const hours = this.getHours();
        const minutes = this.getMinutes();
        const seconds = this.getSeconds();

        const isInvalidDate = isNaN(day) || isNaN(month) || isNaN(years);
        if (isInvalidDate) return '!invalid';

        const dateText = `${years}-${this.leftPad(month)}-${this.leftPad(day)}`;
        const timeText = `T${this.leftPad(hours)}:${this.leftPad(
            minutes
        )}:${this.leftPad(seconds)}`;

        const formattedText = dateText + timeText;
        return formattedText;
    };

    parseJSON = (dateJson: string) => {
        if (dateJson == null) {
            this.resetDate();
            this.dateIsNull = true;
            return;
        }

        const jsonDate = JSON.parse(dateJson);
        const jsonDateString: string | null = jsonDate.value;
        const isValid = jsonDateString != null && jsonDate.utc != null;
        if (isValid) {
            if (jsonDateString == '!null') {
                this.resetDate();
                this.dateIsNull = true;
            } else {
                const dateTimeParts: string[] = jsonDateString.split('T');

                const dateText = dateTimeParts[0];
                const dateValues = this.parseDateValues(dateText);

                const timeText = dateTimeParts[1];
                const timeValues = this.parseTimeValues(timeText);

                this.resetDate();
                this.setUTC(jsonDate.utc == '1');

                if (dateValues != null) {
                    this.dateValue = new Date(
                        dateValues.year,
                        dateValues.month - 1,
                        dateValues.day
                    );
                }

                if (timeValues != null) {
                    this.setHours(timeValues.hours);
                    this.setMinutes(timeValues.minutes);
                    this.setSeconds(timeValues.seconds);
                }
            }
        }
    };

    parseDateValues = (dateText: string) => {
        const [yyyy, mm, dd] = dateText.split('-');
        return {
            year: parseInt(yyyy, 10),
            month: parseInt(mm, 10),
            day: parseInt(dd, 10)
        };
    };

    parseTimeValues = (timeText: string) => {
        const [hh, mm, ss] = timeText.split(':');
        return {
            hours: parseInt(hh, 10),
            minutes: parseInt(mm, 10),
            seconds: parseInt(ss, 10)
        };
    };

    setUTC = (isUTC: boolean) => {
        this.isUTC = isUTC;
    };

    getUTC = (): boolean => {
        return this.isUTC;
    };

    getUTCValue = (isUtc: boolean | undefined): boolean => {
        const utcValue = isUtc != undefined ? isUtc : this.isUTC;
        return utcValue;
    };

    getYear = (isUtc?: boolean) => {
        let year = -1;

        if (this.dateValue !== null) {
            if (this.getUTCValue(isUtc)) year = this.dateValue.getUTCFullYear();
            else year = this.dateValue.getFullYear();
        }

        return year;
    };

    setYear = (year: number, isUtc?: boolean) => {
        if (this.dateValue !== null) {
            if (this.getUTCValue(isUtc)) this.dateValue.setUTCFullYear(year);
            else this.dateValue.setFullYear(year);
        }
    };

    getMonth = (isUtc?: boolean) => {
        let month = -1;

        if (this.dateValue !== null) {
            if (this.getUTCValue(isUtc)) month = this.dateValue.getUTCMonth();
            else month = this.dateValue.getMonth();
        }

        const adjustedMonth = month + 1;
        return adjustedMonth;
    };

    setMonth = (month: number, isUtc?: boolean) => {
        const adjustedMonth = month - 1;

        if (this.dateValue !== null) {
            if (this.getUTCValue(isUtc))
                this.dateValue.setUTCMonth(adjustedMonth);
            else this.dateValue.setMonth(adjustedMonth);
        }
    };

    getDay = (isUtc?: boolean) => {
        let day = -1;

        if (this.dateValue !== null) {
            if (this.getUTCValue(isUtc)) day = this.dateValue.getUTCDate();
            else day = this.dateValue.getDate();
        }

        return day;
    };

    setDay = (day: number, isUtc?: boolean) => {
        if (this.dateValue !== null) {
            if (this.getUTCValue(isUtc)) this.dateValue.setUTCDate(day);
            else this.dateValue.setDate(day);
        }
    };

    getHours = (isUtc?: boolean) => {
        let hours = -1;

        if (this.dateValue !== null) {
            if (this.getUTCValue(isUtc)) hours = this.dateValue.getUTCHours();
            else hours = this.dateValue.getHours();
        }

        return hours;
    };

    setHours = (hours: number, isUtc?: boolean) => {
        if (this.dateValue !== null) {
            if (this.getUTCValue(isUtc)) this.dateValue.setUTCHours(hours);
            else this.dateValue.setHours(hours);
        }
    };

    getMinutes = (isUtc?: boolean) => {
        let minutes = -1;

        if (this.dateValue !== null) {
            if (this.getUTCValue(isUtc))
                minutes = this.dateValue.getUTCMinutes();
            else minutes = this.dateValue.getMinutes();
        }

        return minutes;
    };

    setMinutes = (minutes: number, isUtc?: boolean) => {
        if (this.dateValue !== null) {
            if (this.getUTCValue(isUtc)) this.dateValue.setUTCMinutes(minutes);
            else this.dateValue.setMinutes(minutes);
        }
    };

    getSeconds = (isUtc?: boolean) => {
        let seconds = -1;

        if (this.dateValue !== null) {
            if (this.getUTCValue(isUtc))
                seconds = this.dateValue.getUTCSeconds();
            else seconds = this.dateValue.getSeconds();
        }

        return seconds;
    };

    setSeconds = (seconds: number, isUtc?: boolean) => {
        if (this.dateValue !== null) {
            if (this.getUTCValue(isUtc)) this.dateValue.setUTCSeconds(seconds);
            else this.dateValue.setSeconds(seconds);
        }
    };

    leftPad = (value: number) => {
        if (value < 10) return '0' + value;
        else return value;
    };

    getDateFormatString = (lang: string): string => {
        const formatObj = new Intl.DateTimeFormat(lang).formatToParts(
            new Date()
        );
        return formatObj
            .map((obj) => {
                switch (obj.type) {
                    case 'day':
                        return 'dd';
                    case 'month':
                        return 'MM';
                    case 'year':
                        return 'yyyy';
                    case 'literal':
                        return '/';
                    default:
                        return obj.value;
                }
            })
            .join('');
    };
}
